import axios from 'axios';
import { API_PATH } from '../constants/ApiPath';

const ACTIVITY_LOG_URL = 'https://tri.orion22.com/log-activity';
const PRODUCTION_API = 'https://uniservedapi.orion22.com/api/v1';
const COOKIE_PREFIX = 'activity_logged_';

/**
 * Returns today's date as YYYY-MM-DD string in the user's local timezone.
 */
const getTodayDate = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Returns the current datetime in ISO format (without seconds/ms) for
 * the `work_started_at` payload field.
 */
const getWorkStartedAt = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
};

/**
 * Returns a per-user cookie name for tracking the daily activity log.
 * Uses a hash of the email to keep the cookie name short and safe.
 */
const getCookieName = (email) => {
  // Simple hash to create a short, URL-safe identifier from email
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32-bit integer
  }
  return `${COOKIE_PREFIX}${Math.abs(hash)}`;
};

/**
 * Sets a cookie that expires at the end of the current local day (23:59:59).
 */
const setDailyCookie = (name, value) => {
  const now = new Date();
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  document.cookie = `${name}=${value}; expires=${endOfDay.toUTCString()}; path=/`;
};

/**
 * Reads a cookie value by name. Returns null if not found.
 */
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

/**
 * Deletes a cookie by name.
 */
const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

/**
 * Logs daily user activity by calling the external API.
 * Uses a per-user cookie (survives localStorage.clear on logout) to ensure
 * the API is called only once per user per day.
 *
 * Called from MainApp (the authenticated shell) and SignIn (on login success).
 *
 * API response handling:
 *   200 — success (fresh log or "already logged") → cookie already set
 *   400 — bad payload → cookie stays (won't fix itself, avoid spam)
 *   500 — server error → delete cookie, allow retry on next interaction
 */
export const logDailyActivity = async () => {
  // Only log activity on production — skip staging, dev, localhost
  if (API_PATH !== PRODUCTION_API) return;

  const rawEmail = localStorage.getItem('user_email_id');
  if (!rawEmail) return; // no user logged in

  const userEmail = rawEmail.toLowerCase();
  const cookieName = getCookieName(userEmail);

  // Check if already logged today (cookie exists and matches today's date)
  if (getCookie(cookieName) === getTodayDate()) return;

  // Set the cookie BEFORE the API call to prevent duplicate calls
  // (e.g. SignIn and MainApp both firing logDailyActivity in quick succession)
  setDailyCookie(cookieName, getTodayDate());

  try {
    await axios.post(ACTIVITY_LOG_URL, {
      user_email: userEmail,
      work_started_at: getWorkStartedAt(),
    });
  } catch (error) {
    const status = error?.response?.status;

    if (status === 500 || !error?.response) {
      // Server error or network failure — clear the cookie so it retries next time
      deleteCookie(cookieName);
    }
    // 400 — bad payload, keep the cookie set (won't fix itself, avoid spam)

    // Silently fail — activity logging should never block the user
    console.error('[ActivityLogger] Failed to log daily activity:', error?.message);
  }
};
